import { useEffect, useState, useMemo } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useAdminApi, UsageStats, Company } from "@/hooks/useAdminApi";
import { BarChart3, User, MessageSquare, Volume2, RefreshCw, Calendar, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from "recharts";

const COLORS = {
  linkedin: 'hsl(210, 90%, 55%)',
  messages: 'hsl(142, 70%, 45%)',
  audio: 'hsl(280, 70%, 55%)',
};

export default function AdminStats() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { getUsageStats, listCompanies } = useAdminApi();
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("7days");

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (dateRange) {
      case "today":
        start.setHours(0, 0, 0, 0);
        break;
      case "7days":
        start.setDate(start.getDate() - 7);
        break;
      case "30days":
        start.setDate(start.getDate() - 30);
        break;
      case "90days":
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 7);
    }
    
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
    };
  };

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    const { start_date, end_date } = getDateRange();
    
    const [statsResult, companiesResult] = await Promise.all([
      getUsageStats({ 
        company_id: selectedCompany !== "all" ? selectedCompany : undefined,
        start_date,
        end_date
      }),
      listCompanies({ limit: 100 })
    ]);
    
    if (statsResult.success && statsResult.data) {
      setStats(statsResult.data);
    } else {
      setError(statsResult.error || t('stats.errorLoading'));
    }
    
    if (companiesResult.success && companiesResult.data) {
      setCompanies(companiesResult.data);
    }
    
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedCompany, dateRange]);

  const pieData = useMemo(() => {
    if (!stats?.by_company?.length) return [];
    
    const totals = stats.by_company.reduce(
      (acc, company) => ({
        linkedin: acc.linkedin + company.linkedin,
        messages: acc.messages + company.messages,
        audio: acc.audio + company.audio,
      }),
      { linkedin: 0, messages: 0, audio: 0 }
    );
    
    return [
      { name: t('stats.linkedinProfiles'), value: totals.linkedin, color: COLORS.linkedin },
      { name: t('stats.messages'), value: totals.messages, color: COLORS.messages },
      { name: t('stats.audio'), value: totals.audio, color: COLORS.audio },
    ].filter(item => item.value > 0);
  }, [stats, t]);

  const chartData = useMemo(() => {
    if (!stats?.chart_data?.length) return [];
    return stats.chart_data.map(item => ({
      ...item,
      date: new Date(item.date).toLocaleDateString('pl-PL', { 
        day: 'numeric', 
        month: 'short' 
      })
    }));
  }, [stats]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('stats.title')}</h1>
            <p className="text-muted-foreground">
              {t('stats.subtitle')}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-[180px]">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t('stats.allCompanies')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('stats.allCompanies')}</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">{t('stats.today')}</SelectItem>
                <SelectItem value="7days">{t('stats.7days')}</SelectItem>
                <SelectItem value="30days">{t('stats.30days')}</SelectItem>
                <SelectItem value="90days">{t('stats.90days')}</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {tc('refresh')}
            </Button>
          </div>
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('stats.linkedinProfiles')}</CardTitle>
              <User className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold text-blue-500">
                  {pieData.find(d => d.name === t('stats.linkedinProfiles'))?.value ?? 0}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('stats.messages')}</CardTitle>
              <MessageSquare className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold text-green-500">
                  {pieData.find(d => d.name === t('stats.messages'))?.value ?? 0}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('stats.audio')}</CardTitle>
              <Volume2 className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold text-purple-500">
                  {pieData.find(d => d.name === t('stats.audio'))?.value ?? 0}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                {t('stats.dailyUsage')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Skeleton className="h-full w-full" />
                </div>
              ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="linkedin" 
                      name="LinkedIn" 
                      stroke={COLORS.linkedin} 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="messages" 
                      name={t('stats.messages')} 
                      stroke={COLORS.messages} 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="audio" 
                      name={t('stats.audio')} 
                      stroke={COLORS.audio} 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  {t('stats.noDataForPeriod')}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('stats.usageDistribution')}</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Skeleton className="h-48 w-48 rounded-full" />
                </div>
              ) : pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  {t('stats.noDataForPeriod')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Companies Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('stats.usagePerCompany')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            ) : stats?.by_company && stats.by_company.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-medium">{tc('name')}</th>
                      <th className="text-right py-3 px-2 font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <User className="h-3.5 w-3.5 text-blue-500" />
                          LinkedIn
                        </div>
                      </th>
                      <th className="text-right py-3 px-2 font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <MessageSquare className="h-3.5 w-3.5 text-green-500" />
                          {t('stats.messages')}
                        </div>
                      </th>
                      <th className="text-right py-3 px-2 font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <Volume2 className="h-3.5 w-3.5 text-purple-500" />
                          {t('stats.audio')}
                        </div>
                      </th>
                      <th className="text-right py-3 px-2 font-medium">{tc('total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_company.map((company) => (
                      <tr key={company.company_id} className="border-b border-border last:border-0 hover:bg-muted/50">
                        <td className="py-3 px-2 font-medium">{company.name}</td>
                        <td className="py-3 px-2 text-right text-blue-500">{company.linkedin}</td>
                        <td className="py-3 px-2 text-right text-green-500">{company.messages}</td>
                        <td className="py-3 px-2 text-right text-purple-500">{company.audio}</td>
                        <td className="py-3 px-2 text-right">
                          <Badge variant="secondary">{company.total}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('stats.noUsageData')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
